import { useState } from "react";
import { Camera, Plus, Trash2, FileStack, Check, Wand2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";

function CaptureInput({ onFile, children, className, disabled, testid }) {
  return (
    <label className={className} data-testid={testid}>
      {children}
      <input type="file" accept="image/*" capture="environment" className="hidden" disabled={disabled} onChange={(e) => { const f = e.target.files?.[0]; if (f) onFile(f); e.target.value = ""; }} />
    </label>
  );
}

export function ScanButton({ onFile, label = "Scan with camera", className = "", testid = "scan-button", disabled }) {
  const [pages, setPages] = useState(null);
  const [busy, setBusy] = useState(false);
  const [enhance, setEnhance] = useState(true);
  const addPage = (f) => setPages((p) => [...(p || []), { file: f, url: URL.createObjectURL(f) }]);
  const removePage = (i) => setPages((p) => p.filter((_, idx) => idx !== i));
  const close = () => { (pages || []).forEach((p) => URL.revokeObjectURL(p.url)); setPages(null); };
  const finish = async () => {
    if (!pages?.length) return;
    if (pages.length === 1 && !enhance) { onFile(pages[0].file); close(); return; }
    setBusy(true);
    try {
      const fd = new FormData(); pages.forEach((p) => fd.append("files", p.file)); fd.append("enhance", enhance ? "true" : "false"); fd.append("output", pages.length === 1 ? "image" : "pdf");
      const res = await api.post("/tools/images-to-pdf", fd, { headers: { "Content-Type": "multipart/form-data" }, responseType: "blob" });
      const stamp = new Date().toISOString().slice(0, 10);
      const isPdf = res.data.type === "application/pdf";
      onFile(new File([res.data], isPdf ? `scan-${stamp}.pdf` : `scan-${stamp}.jpg`, { type: isPdf ? "application/pdf" : "image/jpeg" }));
      toast.success(pages.length > 1 ? `${pages.length} pages ${enhance ? "cropped, straightened and " : ""}combined into one PDF` : "Page cropped and straightened"); close();
    } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  };
  return (
    <>
      <CaptureInput onFile={addPage} disabled={disabled} testid={testid} className={`inline-flex items-center gap-1 text-xs text-blue-700 cursor-pointer hover:underline whitespace-nowrap ${disabled ? "opacity-50 pointer-events-none" : ""} ${className}`}>
        <Camera className="h-3.5 w-3.5" />{label}
      </CaptureInput>
      {pages && (
        <Dialog open onOpenChange={(o) => !o && close()}>
          <DialogContent className="max-w-2xl" data-testid="scan-pages-dialog">
            <DialogHeader><DialogTitle className="flex items-center gap-2"><FileStack className="h-5 w-5 text-blue-700" />Scanned pages ({pages.length})</DialogTitle></DialogHeader>
            <p className="text-sm text-slate-600">Snap each page in order. Pages are auto-cropped and straightened like a flatbed scan; several pages are combined into a single PDF.</p>
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-3 max-h-[50vh] overflow-auto p-1">
              {pages.map((p, i) => (
                <div key={p.url} className="relative rounded-md border border-slate-200 overflow-hidden bg-slate-50" data-testid={`scan-page-${i}`}>
                  <img src={p.url} alt={`Page ${i + 1}`} className="h-36 w-full object-cover" />
                  <div className="absolute top-1 left-1 bg-slate-900/80 text-white text-[10px] font-mono px-1.5 rounded">{i + 1}</div>
                  <button type="button" onClick={() => removePage(i)} className="absolute top-1 right-1 bg-white/90 rounded p-0.5" data-testid={`scan-page-remove-${i}`}><Trash2 className="h-3.5 w-3.5 text-rose-600" /></button>
                </div>
              ))}
              <CaptureInput onFile={addPage} testid="scan-add-page" className="h-36 rounded-md border-2 border-dashed border-blue-300 bg-blue-50/50 flex flex-col items-center justify-center gap-1 text-blue-700 text-xs cursor-pointer hover:bg-blue-50">
                <Plus className="h-5 w-5" />Add page
              </CaptureInput>
            </div>
            <DialogFooter className="items-center sm:justify-between gap-3">
              <label className="flex items-center gap-2 text-xs text-slate-700 cursor-pointer mr-auto" data-testid="scan-enhance-toggle"><Switch checked={enhance} onCheckedChange={setEnhance} /><Wand2 className="h-3.5 w-3.5 text-blue-700" />Auto-crop, straighten &amp; clean up</label>
              <div className="flex gap-2">
                <Button variant="outline" onClick={close} data-testid="scan-cancel">Cancel</Button>
                <Button onClick={finish} disabled={busy || pages.length === 0} className="bg-blue-700 hover:bg-blue-800" data-testid="scan-done"><Check className="h-4 w-4 mr-1" />{busy ? "Processing…" : pages.length > 1 ? `Use ${pages.length} pages as PDF` : "Use this page"}</Button>
              </div>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}
